<template>
    <div class="live-sales">
        <header class="live-sales__header">
            <div class="live-sales__heading">
                <h1 class="live-sales__title">Live Sales</h1>
                <p v-if="houseName" class="live-sales__house">{{ houseName }}</p>
            </div>
            <Button
                type="button"
                label="Refresh"
                icon="pi pi-refresh"
                severity="secondary"
                :loading="loading"
                @click="emit('refresh')"
            />
        </header>

        <Message v-if="failure" severity="error" :closable="false" class="live-sales__failure">
            {{ failure }}
        </Message>

        <p v-else-if="!loading && sales.length === 0" class="live-sales__empty">
            No sales are running at {{ houseName || 'this house' }} right now.
        </p>

        <section v-for="sale in sales" :key="sale.id" class="live-sales__sale">
            <div class="live-sales__sale-header">
                <h2 class="live-sales__sale-name">{{ sale.name }}</h2>
                <span class="live-sales__lane-count">{{ laneCountLabel(sale) }}</span>
            </div>
            <div class="live-sales__lanes">
                <LiveSaleCard
                    v-for="lane in sale.lanes"
                    :key="lane.eventId"
                    :name="lane.laneName"
                    :sale-type="lane.saleType"
                    :where="whereOf(sale, lane)"
                    :run-count="lane.runCount"
                    live
                    :live-label="lane.status === 'PAUSED' ? 'PAUSED' : 'LIVE'"
                    join-label="Open Clerk"
                    :joinable="lane.clerkable"
                    @join="openClerk(lane)"
                    @open="openClerk(lane)"
                />
            </div>
        </section>
    </div>
</template>

<script setup>
import { LiveSaleCard } from '@hds/auto-auction-com-components-desktop'
import Button from 'primevue/button'
import Message from 'primevue/message'

// The Live Sales page, as a composition: one block per running sale, and in
// each block one card per lane. The card is the desktop library's LiveSaleCard
// — drawn for the buyer marketplace's LIVE NOW rail, and the same question
// here: what is running, and one button to go there. Its "on My List" clause
// is a dealer's fact and stays hidden at zero.
//
// Presentation only. The view next door reads the stores and turns
// `open-clerk` into a route; this renders what it is handed.
defineOptions({ name: 'LiveSalesBoard' })

defineProps({
    // `[{ id, name, location, lanes: [{ eventId, laneName, saleType, clerkable, status, runCount }] }]` — see lib/liveSales.js.
    sales: { type: Array, default: () => [] },
    houseName: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    // Already a sentence; the view decides what a failure says.
    failure: { type: String, default: '' }
})

const emit = defineEmits(['open-clerk', 'refresh'])

// Every running lane shows, but only a lane a clerk runs opens one.
function openClerk(lane) {
    if (lane.clerkable) {
        emit('open-clerk', lane.eventId)
    }
}

function laneCountLabel(sale) {
    const count = sale.lanes.length
    if (count === 1) {
        return '1 lane running'
    }
    return `${count} lanes running`
}

// "Corona · Paused" — where the lane runs, and its state when that is not
// simply live. The card's badge carries the state too; this is the sentence.
function whereOf(sale, lane) {
    const parts = [sale.location ?? lane.location]
    if (lane.status === 'PAUSED') {
        parts.push('Paused')
    }
    return parts.filter(Boolean).join(' · ')
}
</script>

<style scoped>
.live-sales {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 28px;
}

.live-sales__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.live-sales__title {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--p-text-color);
}

.live-sales__house {
    margin: 4px 0 0;
    font-size: 13px;
    color: var(--p-text-muted-color);
}

.live-sales__failure {
    flex: 0 0 auto;
}

.live-sales__empty {
    margin: 0;
    padding: 40px 0;
    text-align: center;
    color: var(--p-text-muted-color);
}

.live-sales__sale {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.live-sales__sale-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
}

.live-sales__sale-name {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--p-text-color);
}

.live-sales__lane-count {
    font-size: 12px;
    color: var(--p-text-muted-color);
}

.live-sales__lanes {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}
</style>
